// Function index:
//   BodyKind / BODY_PARTS — humanoid silhouettes (24-unit-tall figures in a
//       0 0 40 60 box, ground y=60, facing right, currentColor only), each
//       with a weaponAnchor where a weapon part attaches.
//   BODY_KEYWORDS / bodyForClass(cls) — ordered substring map to a body;
//       fallback 'generic'. combat/cosmetics.ts now sends the DERIVED
//       characters/archetype.ts archetype id here, not raw characters.class —
//       the six archetype ids are themselves among the keywords each group
//       matches, so this keeps resolving correctly. The wider raw-class
//       keyword list (e.g. "paladin", "druid") is kept for legacy persisted
//       combat.started events from before cosmetics.ts derived the id.

import type { BodyDef } from './types.js';

export type BodyKind = 'warrior' | 'ranger' | 'mage' | 'rogue' | 'cleric' | 'generic';

export const BODY_PARTS: Record<BodyKind, BodyDef> = {
  warrior: {
    width: 40,
    weaponAnchor: { x: 27, y: 26 },
    svg: '<rect x="14" y="18" width="12" height="26" rx="4" fill="currentColor"/>' +
      '<circle cx="20" cy="12" r="6" fill="currentColor"/>' +
      '<line x1="16" y1="44" x2="13" y2="60" stroke="currentColor" stroke-width="4"/>' +
      '<line x1="24" y1="44" x2="27" y2="60" stroke="currentColor" stroke-width="4"/>' +
      '<path d="M9 22 a8 8 0 0 0 0 16 Z" fill="currentColor"/>',
  },
  ranger: {
    width: 40,
    weaponAnchor: { x: 28, y: 22 },
    svg: '<rect x="15" y="20" width="10" height="24" rx="4" fill="currentColor"/>' +
      '<circle cx="20" cy="14" r="5.5" fill="currentColor"/>' +
      '<path d="M12 14 l8 -6 l4 4" fill="none" stroke="currentColor" stroke-width="2.5"/>' +
      '<line x1="17" y1="44" x2="15" y2="60" stroke="currentColor" stroke-width="3.5"/>' +
      '<line x1="23" y1="44" x2="25" y2="60" stroke="currentColor" stroke-width="3.5"/>',
  },
  mage: {
    width: 40,
    weaponAnchor: { x: 27, y: 20 },
    svg: '<path d="M13 44 L20 18 L27 44 Z" fill="currentColor"/>' +
      '<circle cx="20" cy="13" r="5.5" fill="currentColor"/>' +
      '<path d="M14 10 L20 2 L26 10 Z" fill="currentColor"/>' +
      '<path d="M13 44 L11 60 M27 44 L29 60" stroke="currentColor" stroke-width="3.5"/>',
  },
  rogue: {
    width: 40,
    weaponAnchor: { x: 27, y: 30 },
    svg: '<rect x="15" y="20" width="10" height="22" rx="4" fill="currentColor"/>' +
      '<path d="M14 12 a6 6 0 0 1 12 0 l-1 5 h-10 Z" fill="currentColor"/>' +
      '<line x1="17" y1="42" x2="14" y2="60" stroke="currentColor" stroke-width="3.5"/>' +
      '<line x1="23" y1="42" x2="26" y2="60" stroke="currentColor" stroke-width="3.5"/>',
  },
  cleric: {
    width: 40,
    weaponAnchor: { x: 27, y: 24 },
    svg: '<path d="M12 44 Q20 16 28 44 Z" fill="currentColor"/>' +
      '<circle cx="20" cy="12" r="6" fill="currentColor"/>' +
      '<rect x="18.5" y="24" width="3" height="10" fill="currentColor"/><rect x="15.5" y="27" width="9" height="3" fill="currentColor"/>' +
      '<path d="M13 44 L12 60 M27 44 L28 60" stroke="currentColor" stroke-width="3.5"/>',
  },
  generic: {
    width: 40,
    weaponAnchor: { x: 26, y: 26 },
    svg: '<rect x="15" y="19" width="10" height="24" rx="4" fill="currentColor"/>' +
      '<circle cx="20" cy="13" r="5.5" fill="currentColor"/>' +
      '<line x1="17" y1="43" x2="15" y2="60" stroke="currentColor" stroke-width="3.5"/>' +
      '<line x1="23" y1="43" x2="25" y2="60" stroke="currentColor" stroke-width="3.5"/>',
  },
};

// The six server-side archetype ids (characters/archetype.ts::Archetype) are
// among the keywords each group matches — 'adventurer' has none, and falls
// through to the 'generic' default below, same as any other unmapped class.
export const BODY_KEYWORDS: ReadonlyArray<[string[], BodyKind]> = [
  [['warrior', 'fighter', 'knight', 'paladin', 'barbarian', 'soldier'], 'warrior'],
  [['ranger', 'hunter', 'archer', 'scout'], 'ranger'],
  [['mage', 'wizard', 'sorcer', 'warlock'], 'mage'],
  [['rogue', 'thief', 'assassin', 'bard'], 'rogue'],
  [['cleric', 'priest', 'druid', 'monk', 'adept'], 'cleric'],
];

export function bodyForClass(cls: string | null | undefined): BodyKind {
  const c = (cls ?? '').toLowerCase();
  for (const [keywords, kind] of BODY_KEYWORDS) {
    if (keywords.some((k) => c.includes(k))) return kind;
  }
  return 'generic';
}
